import { IPublicClientApplication } from '@azure/msal-browser'
import { protectedResources } from '../authConfig'
import { authorizedGet } from '../utils/authUtils'

export interface TransferSummaryResponse {
  totalEarnings: number
  todaysEarnings: number
  thisMonthEarnings: number
}

export interface TransferByLocationResponse {
  locationId: number
  locationName: string
  activeAds: number
  hostShare: number
}

export interface RecentTransferResponse {
  id: string
  amount: number
  advertiserOrganizationName: string | null
  locationName: string
  deviceName: string
  date: string
  adName: string
}

export interface AutoRenewingAdResponse {
  id: number
  adName: string
  locationName: string
  deviceName: string
  advertiserOrganizationName: string
  nextRenewalDate: string
  numberOfDaysToRunAd: number | null
  lastTransferAmount: number
}

export const fetchEarningsSummary = async (
  instance: IPublicClientApplication,
  organizationId: number,
  locationId?: number,
  targetDate?: Date,
): Promise<TransferSummaryResponse> => {
  const baseUrl = process.env.REACT_APP_BASE_API_URL
  const params = new URLSearchParams()
  if (locationId) {
    params.append('locationId', locationId.toString())
  }
  // Use provided date or default to current date
  const date = targetDate || new Date()

  // Set to local midnight for today
  const localMidnight = new Date(date)
  localMidnight.setHours(0, 0, 0, 0)

  // Calculate start of week (Sunday) in local time
  const localStartOfWeek = new Date(localMidnight)
  localStartOfWeek.setDate(localMidnight.getDate() - localMidnight.getDay())

  // Calculate start of month in local time
  const localStartOfMonth = new Date(
    localMidnight.getFullYear(),
    localMidnight.getMonth(),
    1,
  )

  // Convert local midnights to UTC timestamps
  const utcDay = new Date(localMidnight.getTime()) // Correct: this is local midnight in UTC
  const utcWeek = new Date(localStartOfWeek.getTime()) // local week start as UTC
  const utcMonth = new Date(localStartOfMonth.getTime()) // local month start as UTC

  // Format dates for API
  params.append('day', utcDay.toISOString())
  params.append('week', utcWeek.toISOString())
  params.append('month', utcMonth.toISOString())
  console.log('Fetching earnings summary with params:', params.toString())

  return await authorizedGet<TransferSummaryResponse>(
    instance,
    `${baseUrl}/transfers/host/${organizationId}/summary?${params.toString()}`,
    protectedResources.scopes.read,
  )
}

export const fetchEarningsByLocation = async (
  instance: IPublicClientApplication,
  organizationId: number,
): Promise<TransferByLocationResponse[]> => {
  const baseUrl = process.env.REACT_APP_BASE_API_URL
  return await authorizedGet<TransferByLocationResponse[]>(
    instance,
    `${baseUrl}/transfers/host/${organizationId}/by-location`,
    protectedResources.scopes.read,
  )
}

export const fetchAutoRenewingAds = async (
  instance: IPublicClientApplication,
  organizationId: number,
  locationId?: number,
): Promise<AutoRenewingAdResponse[]> => {
  const baseUrl = process.env.REACT_APP_BASE_API_URL
  return await authorizedGet<AutoRenewingAdResponse[]>(
    instance,
    `${baseUrl}/transfers/host/${organizationId}/auto-renewing`,
    protectedResources.scopes.read,
  )
}

export const fetchRecentTransfers = async (
  instance: IPublicClientApplication,
  organizationId: number,
  locationId?: number,
): Promise<RecentTransferResponse[]> => {
  const baseUrl = process.env.REACT_APP_BASE_API_URL
  const params = new URLSearchParams()
  if (locationId) {
    params.append('locationId', locationId.toString())
  }
  params.append('limit', '10')

  return await authorizedGet<RecentTransferResponse[]>(
    instance,
    `${baseUrl}/transfers/host/${organizationId}/recent?${params.toString()}`,
    protectedResources.scopes.read,
  )
}
